import { SearchOutlined } from "@ant-design/icons";
import { Badge, Button, Input, Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { debounce } from "lodash";
import { useEffect, useRef } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  Beacon,
  BeaconSortingColumn,
  BeaconTableColumns,
  BeaconTableSorting,
  SCBOneDevice,
  SCBOneDevicesSortingColumn,
  SCBOneDevicesTableSorting,
  SortingOrder,
} from "../api/models";
import { PagedApiState } from "../api/useApiState";
import { toAntdPagination } from "../util";
import { BeaconStatus, SCBOneDeviceStatus } from "./BeaconStatus";
import { BeaconConfigModalSingle } from "./configuration/BeaconConfigModalSingle";
import { SCBOneConfigModalSingle } from "./configuration/SCBOneConfigModalSingle";
import { formatLocalizedDateTime } from "../util/formatting";

interface BeaconTableProps<T extends object, U> {
  beaconData: PagedApiState<T>;
  visibleColumns: BeaconTableColumns[];
  tableType?: "beacons" | "scb-one";
  disableCellClick?: boolean;
  showPagination?: boolean;
  showFooter?: boolean;
  showClearSelected?: boolean;
  rowsSelectable?: boolean;
  showBeaconFilter?: boolean;
  showSorter?: boolean;
  sortableColumns?: BeaconTableColumns[];
  footerContent?: React.ReactNode;
  tableHeightOffset?: String;
  onSort?: (sorting: U) => void;
  onFilter?: (filter: string) => void;
  onSelect?: (selectedRowKeys: string[]) => void;
}

export function BeaconTableBase<T extends object, U>({
  beaconData,
  visibleColumns,
  tableType = "beacons",
  disableCellClick = false,
  showPagination = true,
  showFooter = false,
  showClearSelected = false,
  rowsSelectable = false,
  showBeaconFilter = false,
  showSorter = true,
  sortableColumns = [],
  footerContent,
  tableHeightOffset = "250px",
  onSort,
  onFilter,
  onSelect,
}: BeaconTableProps<T, U>) {
  const { t } = useTranslation();
  const history = useHistory();
  const isPageRendered = useRef<boolean>(false);
  const filter = useRef<string>("");
  const selectedRowKeys = useRef<string[]>([]);

  useEffect(() => {
    if (!isPageRendered.current) {
      isPageRendered.current = true;
    }
  }, []);

  const onSortingChange = (
    sorter:
      | SorterResult<Beacon | SCBOneDevice>
      | SorterResult<Beacon | SCBOneDevice>[]
  ) => {
    const sort = Array.isArray(sorter) ? sorter[0] : sorter;
    if (onSort) {
      if (tableType === "scb-one") {
        onSort(mapSortingToApiDefintions(sort, tableType) as U);
      } else {
        onSort(mapSortingToApiDefintions(sort, tableType) as U);
      }
    }
  };

  /*
    When user filters on any page that is not the first page (page 0), the table needs to go back to page 0
    to display the results. When the user removes the filter, the table needs to go back to the page it was 
    before filtering took place. 

    This is done by keeping track of the current page in a ref and updating it when the user filters.
  */
  const previousPage = useRef<number>(0);
  const isFiltering = useRef<boolean>(false);

  const onFilterChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onFilter) return;

    if (!isFiltering.current) {
      previousPage.current = beaconData.currentPage;
    }

    if (e.target.value === "") {
      isFiltering.current = false;
      beaconData.goToPage(previousPage.current);
    } else {
      isFiltering.current = true;
      beaconData.goToPage(0);
    }

    filter.current = e.target.value;
    onFilter(e.target.value);
  }, 300);

  const onRowSelectChange = (selectedKeys: React.Key[]) => {
    if (onSelect) onSelect(selectedKeys as string[]);
    selectedRowKeys.current = selectedKeys as string[];
  };

  const clearAllSelectedRows = () => {
    if (onSelect) onSelect([] as string[]);
    selectedRowKeys.current = [];
  };

  const handleCellClick = (urlPathPart: string, id: string) => {
    if (urlPathPart === "/construction-projects/" && id) {
      setTimeout(
        () =>
          history.push(urlPathPart + "?beacons", {
            selectedId: id,
          }),
        0
      );
    } else if (id) {
      setTimeout(() => history.push(urlPathPart + id), 0);
    }
  };

  let visibleColumnsDefintion: any[] = [];
  if (tableType === "scb-one") {
    visibleColumnsDefintion = getVisibleColumnDefinitionsSCBOne(
      t,
      handleCellClick,
      visibleColumns,
      disableCellClick,
      showSorter,
      sortableColumns
    );
  } else if (tableType === "beacons") {
    visibleColumnsDefintion = getVisibleColumnDefinitionsBeacons(
      t,
      handleCellClick,
      visibleColumns,
      disableCellClick,
      showSorter,
      sortableColumns
    );
  }

  return (
    <>
      {showBeaconFilter && (
        <div style={{ display: "flex", alignItems: "center" }}>
          {beaconFilter(onFilterChange, filter.current, t)}
        </div>
      )}
      <Table<T>
        dataSource={beaconData.value?.items ?? []}
        loading={beaconData.loading}
        columns={visibleColumnsDefintion}
        size="middle"
        pagination={
          showPagination ? toAntdPagination(beaconData, false) : false
        }
        onChange={
          showSorter
            ? (_, __, sorter, extra) =>
                onSortingChange(
                  sorter as
                    | SorterResult<Beacon | SCBOneDevice>
                    | SorterResult<Beacon | SCBOneDevice>[]
                )
            : undefined
        }
        scroll={{ y: `calc(100vh - ${tableHeightOffset})` }}
        rowKey="serial"
        rowSelection={
          rowsSelectable
            ? {
                selectedRowKeys: selectedRowKeys.current,
                onChange: onRowSelectChange,
              }
            : undefined
        }
        footer={
          showFooter
            ? showClearSelected
              ? rowSelectFooter(
                  selectedRowKeys.current,
                  clearAllSelectedRows,
                  t,
                  footerContent
                )
              : () => footerContent
            : undefined
        }
      />
    </>
  );
}

const getVisibleColumnDefinitionsSCBOne = (
  t: TFunction,
  handleCellClick: (urlPathPart: string, id: string) => void,
  visibleColumns: BeaconTableColumns[],
  disableCellClick: boolean,
  showSorter?: boolean,
  sortableColumns?: BeaconTableColumns[]
) => {
  const columns = [
    {
      key: "Serial",
      title: t("beacon:serial"),
      sorter: sortableColumns?.includes(BeaconTableColumns.Serial)
        ? showSorter
        : false,
      onCell: (d: SCBOneDevice) =>
        !disableCellClick
          ? {
              onClick: () => {
                handleCellClick("/scb-one/", d.serial);
              },
              style: { cursor: "pointer" },
            }
          : {},
      render: (d: SCBOneDevice) => d.serial,
    },
    {
      key: "OnOffSwitch",
      title: t("beacon:status"),
      sorter: sortableColumns?.includes(BeaconTableColumns.Status)
        ? showSorter
        : false,
      render: (d: SCBOneDevice) => <SCBOneDeviceStatus device={d} />,
    },
    {
      key: "OrganizationName",
      title: t("translations:organization"),
      sorter: sortableColumns?.includes(BeaconTableColumns.OrganizationName)
        ? showSorter
        : false,
      onCell: (d: SCBOneDevice) =>
        !disableCellClick
          ? {
              onClick: () => {
                handleCellClick("/organizations/", d.org?.id ?? "");
              },
              style: d.org ? { cursor: "pointer" } : { cursor: "default" },
            }
          : {},
      render: (d: SCBOneDevice) => d.org?.name,
    },
    {
      key: "LastMessageReceivedAt",
      title: t("beacon:last-message-received-at"),
      sorter: sortableColumns?.includes(
        BeaconTableColumns.LastMessageReceivedAt
      )
        ? showSorter
        : false,
      render: (d: SCBOneDevice) =>
        d.lastMsgAt !== null ? formatLocalizedDateTime(d.lastMsgAt) : "",
    },
    {
      key: "ConfigurationButton",
      width: 100,
      sorter: false,
      render: (d: SCBOneDevice) => (
        <SCBOneConfigModalSingle selectedSerial={d.serial} />
      ),
    },
  ];

  return columns.filter((column) =>
    visibleColumns.includes(column.key as BeaconTableColumns)
  );
};

const getVisibleColumnDefinitionsBeacons = (
  t: TFunction,
  handleCellClick: (urlPathPart: string, id: string) => void,
  visibleColumns: BeaconTableColumns[],
  disableCellClick: boolean,
  showSorter?: boolean,
  sortableColumns?: BeaconTableColumns[] | undefined
) => {
  const columns = [
    {
      key: "Serial",
      title: t("beacon:serial"),
      render: (b: Beacon) => b.serial,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.Serial)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
      onCell: (b: Beacon) =>
        !disableCellClick
          ? {
              onClick: () => {
                handleCellClick("/beacons/", b.serial);
              },
              style: { cursor: "pointer" },
            }
          : {},
    },
    {
      key: "OnOffSwitch",
      title: t("beacon:status"),
      render: (b: Beacon) => <BeaconStatus beacon={b} />,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.Status)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
    },
    {
      key: "Iccid",
      title: t("beacon:iccid"),
      render: (b: Beacon) => b.iccid,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.Iccid)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
    },
    {
      key: "Hwv",
      title: t("beacon:hardware-version"),
      render: (b: Beacon) => b.hwv,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.Hwv)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
    },
    {
      key: "Fwv",
      title: t("beacon:firmware-version"),
      render: (b: Beacon) => b.fwv,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.Fwv)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
    },
    {
      key: "ConstructionSiteName",
      title: t("translations:construction-site"),
      render: (b: Beacon) => b.site?.name,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.ConstructionSiteName)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
      onCell: (b: Beacon) =>
        !disableCellClick
          ? {
              onClick: () => {
                handleCellClick("/construction-projects/", b.site?.id ?? "");
              },
              style: b.site ? { cursor: "pointer" } : { cursor: "default" },
            }
          : {},
    },
    {
      key: "ConstructionProjectName",
      title: t("translations:construction-project"),
      render: (b: Beacon) => b.project?.name,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(
              BeaconTableColumns.ConstructionProjectName
            )
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
      onCell: (b: Beacon) =>
        !disableCellClick
          ? {
              onClick: () => {
                handleCellClick("/construction-projects/", b.project?.id ?? "");
              },
              style: b.project ? { cursor: "pointer" } : { cursor: "default" },
            }
          : {},
    },
    {
      key: "OrganizationName",
      title: t("translations:organization"),
      render: (b: Beacon) => b.org?.name,
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.OrganizationName)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
      onCell: (b: Beacon) =>
        !disableCellClick
          ? {
              onClick: () => {
                handleCellClick("/organizations/", b.org?.id ?? "");
              },
              style: b.org ? { cursor: "pointer" } : { cursor: "default" },
            }
          : {},
    },
    {
      key: "LastMessageReceivedAt",
      title: t("beacon:last-message-received-at"),
      render: (b: Beacon) =>
        b.lastMsgAt !== null ? formatLocalizedDateTime(b.lastMsgAt) : "",
      sorter:
        showSorter && sortableColumns && sortableColumns.length > 0
          ? sortableColumns?.includes(BeaconTableColumns.LastMessageReceivedAt)
          : showSorter && sortableColumns?.length === 0
          ? true
          : false,
    },
    {
      key: "ConfigurationButton",
      width: 100,
      render: (b: Beacon) => (
        <BeaconConfigModalSingle selectedBeaconSerial={b.serial} />
      ),
    },
  ];

  return columns.filter((column) =>
    visibleColumns.includes(column.key as BeaconTableColumns)
  );
};

const rowSelectFooter = (
  selectedRowIds: string[] | undefined,
  onClearAllSelectedBeacons: () => void,
  t: TFunction,
  footerContent?: React.ReactNode
) => {
  return () => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto auto auto auto auto auto auto auto 1fr",
        gridColumnGap: "1em",
      }}
    >
      <Badge count={selectedRowIds?.length} style={{ backgroundColor: "gray" }}>
        <Button
          disabled={selectedRowIds?.length === 0}
          onClick={() => onClearAllSelectedBeacons()}
        >
          {t("construction-site:clear-selected-beacons")}
        </Button>
      </Badge>
      {footerContent}
    </div>
  );
};

const beaconFilter = (
  onFilterChanged: (e: React.ChangeEvent<HTMLInputElement>) => void,
  filter: string,
  t: TFunction
) => {
  return (
    <Input
      aria-label="beacon-table-filter"
      suffix={<SearchOutlined />}
      placeholder={t("beacon:filter-placeholder")}
      onChange={onFilterChanged}
      allowClear
      style={{ margin: "1rem", width: "25em" }}
    />
  );
};

const mapSortingToApiDefintions = (
  sorting: SorterResult<Beacon | SCBOneDevice>,
  tableType: "beacons" | "scb-one"
): BeaconTableSorting | SCBOneDevicesTableSorting => {
  let sortColumnId:
    | BeaconSortingColumn
    | SCBOneDevicesSortingColumn
    | undefined = undefined;

  if (tableType === "scb-one") {
    sortColumnId =
      SCBOneDevicesSortingColumn[
        sorting.columnKey as keyof typeof SCBOneDevicesSortingColumn
      ];
  } else {
    sortColumnId =
      BeaconSortingColumn[
        sorting.columnKey as keyof typeof BeaconSortingColumn
      ];
  }

  switch (sorting.order) {
    case "ascend":
      return {
        sortColumnId,
        sortOrderId: SortingOrder.Ascending,
      };
    case "descend":
      return {
        sortColumnId,
        sortOrderId: SortingOrder.Descending,
      };
    default:
      return {
        sortColumnId: undefined,
        sortOrderId: undefined,
      };
  }
};
